// @flow
import assert from 'assert';
import supported from '@mapbox/mapbox-gl-supported';

import {version} from '../package.json';
import Map from './ui/map';
import {Common} from './util/enums';
import type {UnitEnum} from './util/enums';
import NavigationControl from './ui/control/navigation_control';
import GeolocateControl from './ui/control/geolocate_control';
import AttributionControl from './ui/control/attribution_control';
import ScaleControl from './ui/control/scale_control';
import FullscreenControl from './ui/control/fullscreen_control';
import WeatherAlertClickControl from './ui/control/weatheralert_click_control';
import WeatherAlertFilterControl from './ui/control/weatheralert_filter_control';
import TruckRestrictionClickControl from './ui/control/truck_restriction_click_control';
import TruckRestrictionFilterControl from './ui/control/truck_restriction_filter_control';
import {ContentBase} from './ui/content-layer/content_base';
import {ClickControlBase} from './ui/control/click_control_base';
import {FilterControlBase} from './ui/control/filter_control_base';
import TrafficIncidentClickControl from './ui/control/traffic_incident_click_control';
import TrafficIncidentFilterControl from './ui/control/traffic_incident_filter_control';
import TrafficCameraClickControl from './ui/control/traffic_camera_click_control';
import PlaceClickControl from './ui/control/place_click_control';
import Popup from './ui/popup';
import Marker from './ui/marker';
import Style from './style/style';
import LngLat from './geo/lng_lat';
import LngLatBounds from './geo/lng_lat_bounds';
import Point from '@mapbox/point-geometry';
import MercatorCoordinate from './geo/mercator_coordinate';
import {Evented} from './util/evented';
import config from './util/config';
import {Debug} from './util/debug';
import {isSafari} from './util/util';
import {setRTLTextPlugin, getRTLTextPluginStatus} from './source/rtl_text_plugin';
import WorkerPool from './util/worker_pool';
import Route from './ui/route';
import {DwellTimes} from './services/dwelltimes';
import {Geocoder} from './services/geocoder';
import {Search} from './services/search';
import {Places} from './services/places';
import {prewarm, clearPrewarmedResources} from './util/global_worker_pool';
import {clearTileCache} from './util/tile_request_cache';
import {PerformanceUtils} from './util/performance';
import CustomRoad from './ui/content-layer/custom_road';
import TrafficIncident from './ui/content-layer/traffic_incident';
import TrafficCamera from './ui/content-layer/traffic_camera';
import TruckRestriction from './ui/content-layer/truck_restriction';
import WeatherRadar from './ui/content-layer/weather_radar';
import WeatherCloud from './ui/content-layer/weather_cloud';
import Traffic from './ui/content-layer/traffic';
import WeatherAlert from './ui/content-layer/weather_alert';
import RoadSurface from './ui/content-layer/road_surface';
import PointsOfInterest from './ui/content-layer/points_of_interest';
import RoadSurfaceLegendControl from './ui/control/roadsurface_legend_control';

import {Routing} from './services/routing';
import {Rail} from './services/rail';
import UndoRedoStack from './util/undo_redo_stack';
import urls from './util/urls';

const exported = {
    version,
    supported,
    setRTLTextPlugin,
    getRTLTextPluginStatus,
    Map,
    NavigationControl,
    GeolocateControl,
    AttributionControl,
    ScaleControl,
    FullscreenControl,
    WeatherAlertClickControl,
    WeatherAlertFilterControl,
    TruckRestrictionFilterControl,
    TruckRestrictionClickControl,
    ContentBase,
    ClickControlBase,
    FilterControlBase,
    TrafficIncidentFilterControl,
    TrafficIncidentClickControl,
    TrafficCameraClickControl,
    PlaceClickControl,
    RoadSurfaceLegendControl,
    Popup,
    Marker,
    Style,
    LngLat,
    LngLatBounds,
    Point,
    MercatorCoordinate,
    Evented,
    config,
    /**
     * Initializes resources like WebWorkers that can be shared across maps to lower load
     * times in some situations. `TrimbleMaps.workerUrl` and `TrimbleMaps.workerCount`, if being
     * used, must be set before `prewarm()` is called to have an effect.
     *
     * By default, the lifecycle of these resources is managed automatically, and they are
     * lazily initialized when a Map is first created. By invoking `prewarm()`, these
     * resources will be created ahead of time, and will not be cleared when the last Map
     * is removed from the page. This allows them to be re-used by new Map instances that
     * are created later. They can be manually cleared by calling
     * `TrimbleMaps.clearPrewarmedResources()`. This is only necessary if your web page remains
     * active but stops using maps altogether.
     *
     * This is primarily useful when using GL-JS maps in a single page app, wherein a user
     * would navigate between various views that can cause Map instances to constantly be
     * created and destroyed.
     *
     * @function prewarm
     * @example
     * TrimbleMaps.prewarm()
     */
    prewarm,
    /**
     * Clears up resources that have previously been created by `TrimbleMaps.prewarm()`.
     * Note that this is typically not necessary. You should only call this function
     * if you expect the user of your app to not return to a Map view at any point
     * in your application.
     *
     * @function clearPrewarmedResources
     * @example
     * TrimbleMaps.clearPrewarmedResources()
     */
    clearPrewarmedResources,
    Common,
    Route,
    DwellTimes,
    Geocoder,
    Search,
    Places,
    CustomRoad,
    TrafficIncident,
    TrafficCamera,
    TruckRestriction,
    WeatherRadar,
    WeatherCloud,
    Traffic,
    WeatherAlert,
    RoadSurface,
    PointsOfInterest,
    Routing,
    Rail,
    UndoRedoStack,

    /**
     * Gets and sets the JavaScript Maps SDK API key.
     *
     * @var {string} APIKey
     *
     * @returns {string} The API Key.
     * @example
     * TrimbleMaps.APIKey = "YOUR_KEY_HERE";
     */
    get APIKey() {
        return config.API_KEY;
    },

    set APIKey(key: string) {
        config.API_KEY = key;
    },

    /**
     * Sets the API Key.
     *
     * @function setAPIKey
     * @param {string} key The API Key.
     */
    setAPIKey(key: string) {
        config.API_KEY = key;
    },

    /**
     * Gets and sets the token to be used for API calls.
     * If set, this token will be used for all non-style related service calls (routing, geocoding, etc.).
     *
     * @var {string} APIToken
     *
     * @returns {string} The API Token.
     * @example
     * TrimbleMaps.APIToken = "YOUR_TOKEN_HERE";
     */
    get APIToken() {
        return config.API_TOKEN;
    },

    set APIToken(token: string) {
        config.API_TOKEN = token;
    },

    /**
     * Sets the API Token.
     * If set, this token will be used for all non-style related service calls (routing, geocoding, etc.).
     *
     * @function setAPIToken
     * @param {string} token The API Token.
     */
    setAPIToken(token: string) {
        config.API_TOKEN = token;
    },

    /**
     * Sets the Unit.
     *
     * @function setUnit
     * @param {UnitEnum} unit The Map unit
     */
    setUnit(unit: UnitEnum) {
        config.UNIT = unit;
    },

    /**
     * Gets and sets the unit to be used for route report calls.
     * If set, this unit will be used for conversions
     *
     * @var {UnitEnum} unit
     *
     * @returns {UnitEnum} The unit.
     * @example
     * TrimbleMaps.UNIT = Common.Unit.ENGLISH;
     */
    get UNIT() {
        return config.UNIT;
    },

    set UNIT(unit: UnitEnum) {
        config.UNIT = unit;
    },

    /**
     * Gets and sets the JavaScript Maps SDK TID Token and External Account Id.
     * If set, this token and accountId will be used for all non-style related service calls (routeModifiers, customPlaces, etc.).
     * @var {Object} token
     *
     * @returns  {Object} tidToken and externalAccountId.
     */
    get TIDToken() {
        return {tidToken:config.TID_TOKEN, externalAccountId:config.EXT_ACCOUNT_ID};
    },
    set TIDToken(token: Object) {
        if (token['tidToken'] !== 'undefined') {
            config.TID_TOKEN = token['tidToken'];
        }
        if (token['externalAccountId'] !== 'undefined') {
            config.EXT_ACCOUNT_ID = token['externalAccountId'];
        }
    },
    /**
     * Sets the TID Token and optional External Account Id.
     * If set, this token and accountId will be used for all non-style related service calls (routeModifiers, customPlaces, etc.).
     *
     * @function setTIDToken
     *  @param {Object} token Object properties should match tokenId and accountId
     */
    setTIDToken(token: Object) {
        if (token['tidToken'] !== 'undefined') {
            config.TID_TOKEN = token['tidToken'];
        }
        if (token['externalAccountId'] !== 'undefined') {
            config.EXT_ACCOUNT_ID = token['externalAccountId'];
        }
    },

    /**
     * Gets and sets the number of web workers instantiated on a page with GL JS maps.
     * By default, it is set to half the number of CPU cores (capped at 6).
     * Make sure to set this property before creating any map instances for it to have effect.
     *
     * @var {string} workerCount
     * @returns {number} Number of workers currently configured.
     * @example
     * TrimbleMaps.workerCount = 2;
     */
    get workerCount(): number {
        return WorkerPool.workerCount;
    },

    set workerCount(count: number) {
        WorkerPool.workerCount = count;
    },

    /**
     * Gets and sets the maximum number of images (raster tiles, sprites, icons) to load in parallel,
     * which affects performance in raster-heavy maps. 16 by default.
     *
     * @var {string} maxParallelImageRequests
     * @returns {number} Number of parallel requests currently configured.
     * @example
     * TrimbleMaps.maxParallelImageRequests = 10;
     */
    get maxParallelImageRequests(): number {
        return config.MAX_PARALLEL_IMAGE_REQUESTS;
    },

    set maxParallelImageRequests(numRequests: number) {
        config.MAX_PARALLEL_IMAGE_REQUESTS = numRequests;
    },

    /**
     * Clears browser storage used by this library. Using this method flushes the tile
     * cache that is managed by this library. Tiles may still be cached by the browser
     * in some cases.
     *
     * This API is supported on browsers where the [`Cache` API](https://developer.mozilla.org/en-US/docs/Web/API/Cache)
     * is supported and enabled. This includes all major browsers when pages are served over
     * `https://`, except Internet Explorer and Edge Mobile.
     *
     * When called in unsupported browsers or environments (private or incognito mode), the
     * callback will be called with an error argument.
     *
     * @function clearStorage
     * @param {Function} callback Called with an error argument if there is an error.
     * @example
     * TrimbleMaps.clearStorage();
     */
    clearStorage(callback?: (err: ?Error) => void) {
        clearTileCache(callback);
    },

    workerUrl: '',

    /**
     * @private
     *
     * Override the service URLs used by the SDK.
     * This should only be used for internal testing and be called before the map is instantiated.
     * If this is called after the map is created, all components that depend on service calls will
     * need to be re-run. This will NOT automatically update the map.
     * For example, the map style will need to reloaded by via a method like setStyle and routes
     * will need to be updated.
     *
     * @param {Object} newUrls Object properties should match the urls build config.
     */
    _setServiceUrls(newUrls: Object) {
        Object.keys(urls).forEach((key) => {
            if (typeof newUrls[key] !== 'undefined') {
                urls[key] = newUrls[key];
            }
        });
    }
};

//This gets automatically stripped out in production builds.
Debug.extend(exported, {isSafari, getPerformanceMetrics: PerformanceUtils.getPerformanceMetrics});

/**
 * The version of the JavaScript Maps SDK in use as specified in `package.json`.
 *
 * @var {string} version
 */

/**
 * Test whether the browser supports the JavaScript Maps SDK.
 *
 * @function supported
 * @param {Object} [options]
 * @param {boolean} [options.failIfMajorPerformanceCaveat=false] If `true`,
 *   the function will return `false` if the performance of the JavaScript Maps SDK would
 *   be dramatically worse than expected (e.g. a software WebGL renderer would be used).
 * @return {boolean}
 * @example
 * TrimbleMaps.supported() // = true
 */

/**
 * Sets the map's RTL text plugin.
 * Necessary for supporting languages like Arabic and Hebrew that are written right-to-left.
 *
 * @function setRTLTextPlugin
 * @param {string} pluginURL URL pointing to the RTL text plugin source.
 * @param {Function} callback Called with an error argument if there is an error.
 * @param {boolean} lazy If set to `true`, will defer loading the plugin until rtl text is encountered,
 *    rtl text will then be rendered only after the plugin finishes loading.
 * @example
 * TrimbleMaps.setRTLTextPlugin('');
 */

/**
 * Gets the map's [RTL text plugin] status.
 * The status can be `unavailable` (i.e. not requested or removed), `loading`, `loaded` or `error`.
 * If the status is `loaded` and the plugin is requested again, an error will be thrown.
 *
 * @function getRTLTextPluginStatus
 * @example
 * const pluginStatus = TrimbleMaps.getRTLTextPluginStatus();
 */

export default exported;

// canary assert: used to confirm that asserts have been removed from production build
assert(true, 'canary assert');
